import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

export function PageShell({ children, maxWidth = "lg", sx }) {
  return (
    <Container
      maxWidth={maxWidth}
      sx={{
        position: "relative",
        pt: { xs: 3, md: 5 },
        pb: { xs: "calc(120px + env(safe-area-inset-bottom))", md: 5 },
        px: { xs: 2, sm: 3 },
        ...sx,
      }}
    >
      {children}
    </Container>
  );
}

export function SurfaceCard({ children, sx, ...props }) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "visible",
        borderRadius: { xs: "28px", md: "32px" },
        border: `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
        background: `linear-gradient(180deg, ${alpha("#ffffff", 0.96)}, ${alpha(
          theme.palette.background.paper,
          0.88
        )})`,
        backdropFilter: "blur(14px)",
        boxShadow: theme.palette.custom.shadow,
        transition:
          "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
        ...sx,
      }}
      {...props}
    >
      {children}
    </Paper>
  );
}

export function PageIntro({ eyebrow, title, subtitle, action, chips = [] }) {
  const theme = useTheme();

  return (
    <SurfaceCard
      sx={{
        mb: { xs: 3, md: 4 },
        px: { xs: 2.75, md: 4.25 },
        py: { xs: 3.25, md: 4 },
        background: `linear-gradient(135deg, ${alpha("#ffffff", 0.96)} 0%, ${alpha(
          theme.palette.primary.light,
          0.10
        )} 100%)`,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "flex-end" }}
      >
        <Box maxWidth={720}>
          {eyebrow && (
            <Chip
              label={eyebrow}
              size="small"
              sx={{
                mb: 1.5,
                maxWidth: "100%",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.dark,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                fontWeight: 800,
                "& .MuiChip-label": {
                  px: 1.2,
                },
              }}
            />
          )}
          <Typography variant="h2" sx={{ mb: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="subtitle1" sx={{ maxWidth: 680 }}>
              {subtitle}
            </Typography>
          )}
          {chips.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 2.25 }}
            >
              {chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  variant="outlined"
                  sx={{ bgcolor: alpha("#ffffff", 0.74) }}
                />
              ))}
            </Stack>
          )}
        </Box>

        {action && <Box sx={{ width: { xs: "100%", md: "auto" } }}>{action}</Box>}
      </Stack>
    </SurfaceCard>
  );
}

export function StatStrip({ items = [] }) {
  const theme = useTheme();

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={{ mb: 3, width: "100%" }}
    >
      {items.map((item) => (
        <SurfaceCard
          key={item.label}
          sx={{
            flex: 1,
            px: { xs: 2.4, md: 2.8 },
            py: { xs: 2.2, md: 2.5 },
            minHeight: { xs: 96, md: 108 },
            background: alpha(item.tint || theme.palette.primary.main, 0.06),
          }}
        >
          <Stack spacing={0.8}>
            <Typography variant="body2" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: "1.6rem", md: "2rem" } }}>
              {item.value}
            </Typography>
            {item.caption && (
              <Typography variant="body2" color="text.secondary">
                {item.caption}
              </Typography>
            )}
          </Stack>
        </SurfaceCard>
      ))}
    </Stack>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  actionTo,
  action,
}) {
  const theme = useTheme();

  return (
    <SurfaceCard
      sx={{
        textAlign: "center",
        px: { xs: 2.75, md: 4 },
        py: { xs: 4, md: 5 },
        borderStyle: "solid",
        borderColor: alpha(theme.palette.primary.main, 0.12),
      }}
    >
      <Stack spacing={1.5} alignItems="center">
        {icon}
        <Typography variant="h4">{title}</Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 460, mx: "auto" }}
        >
          {description}
        </Typography>
        {action ||
          (actionLabel && actionTo ? (
            <Button component={RouterLink} to={actionTo} variant="contained">
              {actionLabel}
            </Button>
          ) : null)}
      </Stack>
    </SurfaceCard>
  );
}

export function SectionBlock({ title, subtitle, action, children, sx }) {
  return (
    <Box sx={{ mb: 3.2, ...sx }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Box>
  );
}
